import { LoadingOverlay, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import useUpdateChargingServiceMetadata from '../../../hooks/charging-services/useUpdateChargingServiceMetadata.ts';
import {
  UpdateChargingServiceMetadataRequest,
  UpdateChargingServiceOperationHoursRequest,
  UpdateChargingServicePaymentMethodsRequest,
  UpdateChargingServiceTagsRequest,
} from '../../../api/requests/charging-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import useChargingService from '../../../hooks/charging-services/useChargingService.ts';
import useUpdateChargingServiceOperationHours from '../../../hooks/charging-services/useUpdateChargingServiceOperationHours.ts';
import useUpdateChargingServiceStoreIds from '../../../hooks/charging-services/useUpdateChargingServiceStoreIds.ts';
import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import useEnableChargingService from '../../../hooks/charging-services/useEnableChargingService.ts';
import useDisableChargingService from '../../../hooks/charging-services/useDisableChargingService.ts';
import usePauseChargingService from '../../../hooks/charging-services/usePauseChargingService.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';
import MutateUpdateChargingServiceForm, {
  MutateUpdateChargingServiceFormValues,
} from '../../ui/places/MutateUpdateChargingServiceForm.tsx';
import { PhoneNumberUtil } from 'google-libphonenumber';
import useUpdateChargingServiceTags from '../../../hooks/charging-services/useUpdateChargingServiceTags.ts';
import useUpdateChargingServicePaymentMethods from '../../../hooks/charging-services/useUpdateChargingServicePaymentMethods.ts';
import {
  buildStoreUrl,
  resolveStoreIdsChange,
} from '../../../utils/storeUrl.ts';

const phoneUtil = PhoneNumberUtil.getInstance();

interface UpdateChargingServiceMetadataModalProps {
  placeId: string;
  chargingServiceId: string | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const UpdateChargingServiceMetadataDrawer = ({
  placeId,
  chargingServiceId,
  onSuccess,
  onCancel,
}: UpdateChargingServiceMetadataModalProps) => {
  const queryClient = useQueryClient();

  const { data: chargingService, isLoading: isChargingServiceLoading } =
    useChargingService(chargingServiceId);

  const { mutateAsync: updateChargingServiceMetadata } =
    useUpdateChargingServiceMetadata();
  const { mutateAsync: updateChargingServiceOperationHours } =
    useUpdateChargingServiceOperationHours();
  const { mutateAsync: updateChargingServiceStoreIds } =
    useUpdateChargingServiceStoreIds();
  const { mutateAsync: updateChargingServiceTags } =
    useUpdateChargingServiceTags();
  const { mutateAsync: updateChargingServicePaymentMethods } =
    useUpdateChargingServicePaymentMethods();
  const { mutateAsync: enableChargingService } = useEnableChargingService();
  const { mutateAsync: pauseChargingService } = usePauseChargingService();
  const { mutateAsync: disableChargingService } = useDisableChargingService();

  const { mutate: handleSubmitAll, isPending: isUpdateChargingServicePending } =
    useMutation<void, ErrorResponse, MutateUpdateChargingServiceFormValues>({
      mutationFn: async (formValues) => {
        const storeIdsToSubmit = resolveStoreIdsChange(
          formValues.storeIdUrl,
          'CHARGING',
          chargingService.storeIds,
        );
        if (storeIdsToSubmit !== null) {
          await updateChargingServiceStoreIds({
            serviceId: chargingService.id,
            storeIds: storeIdsToSubmit,
          });
        }

        const parsedPhoneNumber =
          formValues.phoneNumberCountryCode !== '' &&
          formValues.phoneNumberValue !== ''
            ? phoneUtil.parse(
                `${formValues.phoneNumberCountryCode}${formValues.phoneNumberValue}`,
              )
            : null;

        const updateChargingServiceMetadataRequest: UpdateChargingServiceMetadataRequest =
          {
            serviceId: chargingService.id,
            name: formValues.name.trim(),
            brandId: formValues.brandId ?? '',
            phoneNumber: {
              countryCode: formValues.phoneNumberCountryCode ?? '',
              number: parsedPhoneNumber
                ? phoneUtil.getNationalSignificantNumber(parsedPhoneNumber)
                : '',
            },
            website: formValues.website,
            notes: formValues.notes,
            pricingSource: chargingService.pricingSource,
          };

        await updateChargingServiceMetadata(
          updateChargingServiceMetadataRequest,
        );

        if (formValues.serviceStatus === 'ONLINE') {
          await enableChargingService(chargingService.id);
        } else if (formValues.serviceStatus === 'TEMPORARY_OFFLINE') {
          await pauseChargingService(chargingService.id);
        } else {
          await disableChargingService(chargingService.id);
        }

        const updateChargingServiceTagsRequest: UpdateChargingServiceTagsRequest =
          {
            serviceId: chargingService.id,
            tags: formValues.tags,
          };

        await updateChargingServiceTags(updateChargingServiceTagsRequest);

        const updateChargingServicePaymentMethodsRequest: UpdateChargingServicePaymentMethodsRequest =
          {
            serviceId: chargingService.id,
            paymentMethods: formValues.paymentMethods,
          };

        await updateChargingServicePaymentMethods(
          updateChargingServicePaymentMethodsRequest,
        );

        // 一律送出表單當下顯示的營業時間，確保 drawer 上看到的資料跟實際寫入的完全一致
        const updateChargingServiceOperationHoursRequest: UpdateChargingServiceOperationHoursRequest =
          {
            serviceId: chargingServiceId,
            operationHours: formValues.operationHours,
          };

        await updateChargingServiceOperationHours(
          updateChargingServiceOperationHoursRequest,
        );
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['chargingServices', chargingService.id],
          exact: true,
        });
        await queryClient.invalidateQueries({
          queryKey: ['places', placeId],
          exact: true,
        });
        notifySuccess('更新充電服務成功');
        onSuccess();
      },
      onError: (error) => {
        notifyError('更新充電服務失敗', error.message);
      },
    });

  const handleFormSubmit = (
    formValues: MutateUpdateChargingServiceFormValues,
  ) => {
    let storeIdsToSubmit: ReturnType<typeof resolveStoreIdsChange>;
    try {
      storeIdsToSubmit = resolveStoreIdsChange(
        formValues.storeIdUrl,
        'CHARGING',
        chargingService.storeIds,
      );
    } catch {
      // 網址格式錯誤已經在表單驗證擋下，理論上不會走到這裡
      return;
    }

    if (storeIdsToSubmit === null) {
      handleSubmitAll(formValues);
      return;
    }

    const isUnbinding = storeIdsToSubmit.length === 0;
    modals.openConfirmModal({
      title: (
        <Text style={{ fontSize: 16, fontWeight: 700 }}>
          {isUnbinding ? '解除綁定商店' : '綁定商店'}
        </Text>
      ),
      children: (
        <Text size="sm">
          {isUnbinding
            ? '解除綁定後，目前的營業時間不會被清空，會維持最後一次同步的資料，確定要繼續嗎？'
            : '綁定後將會用 autoplus 現有的營業時間覆蓋這個服務目前的資料，確定要繼續嗎？'}
        </Text>
      ),
      labels: { confirm: '是，我確定', cancel: '取消' },
      confirmProps: isUnbinding ? { color: 'red' } : undefined,
      onConfirm: () => handleSubmitAll(formValues),
    });
  };

  const handleClose = () => {
    onCancel();
  };

  if (isChargingServiceLoading) return <LoadingOverlay />;

  return (
    <MutateUpdateChargingServiceForm
      initialValues={{
        serviceId: chargingService.id,
        name: chargingService.name,
        serviceStatus: chargingService.serviceStatus,
        brandId: chargingService.brandId,
        storeIdUrl: chargingService.storeIds[0]
          ? buildStoreUrl(chargingService.storeIds[0])
          : '',
        operationHours: chargingService.operationHours,
        phoneNumberCountryCode: chargingService.phoneNumber?.countryCode,
        phoneNumberValue: chargingService.phoneNumber?.number,
        website: chargingService.website,
        notes: chargingService.notes,
        tags: chargingService.tags,
        paymentMethods: chargingService.paymentMethods,
      }}
      onSubmit={handleFormSubmit}
      onCancel={handleClose}
      isSubmitting={isUpdateChargingServicePending}
      isUpdating={true}
    />
  );
};

export { UpdateChargingServiceMetadataDrawer };
